import React from "react";
import { Link } from "react-router-dom";
import styles from "./CommentMessage.module.css";

type Recipient = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  preferredName: string;
};

type HashTag = {
  hashTagId?: number;
  hashTagName?: string;
};

type CommentMessageProps = {
  message?: string;
  pointsGiven?: number;
  coinsGiven?: number;
  recipients?: Recipient[] | null;
  hashTagIds?: HashTag[] | null;
};

/**
 * Renders a comment message with formatted chips:
 * - @mentions → gold chips linked to user profiles
 * - #hashtags → cyan chips
 * - +points → cyan badge showing "X pts"
 * - $coins → gold badge showing "X coins"
 */
const CommentMessage = ({
  message,
  pointsGiven,
  coinsGiven,
  recipients,
  hashTagIds,
}: CommentMessageProps) => {
  if (!message) return null;

  // Build regex patterns
  const patterns: string[] = [];

  // @mentions
  if (recipients && recipients.length > 0) {
    const nicknames = recipients.map((user) =>
      user.preferredName.replace(/\s+/g, "")
    );
    const escapedNicknames = nicknames.map((n) =>
      n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    patterns.push(`@(?:${escapedNicknames.join("|")})(?=\\s|$|\\b)`);
  }

  // #hashtags
  if (hashTagIds && hashTagIds.length > 0) {
    const tagNames = hashTagIds.map((t) =>
      (t.hashTagName || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    patterns.push(`#(?:${tagNames.join("|")})(?=\\s|$|\\b)`);
  }

  // +points
  if (pointsGiven && pointsGiven > 0) {
    patterns.push(`\\+\\d+(?=\\s|$)`);
  }

  // $coins
  if (coinsGiven && coinsGiven > 0) {
    patterns.push(`\\$\\d+(?=\\s|$)`);
  }

  // If no patterns to match, just return plain text
  if (patterns.length === 0) {
    return <span>{message}</span>;
  }

  const combinedPattern = new RegExp(`(${patterns.join("|")})`, "gi");
  const parts = message.split(combinedPattern);

  const rendered = parts.map((part, index) => {
    if (!part) return null;

    // Check @mention
    if (part.startsWith("@") && recipients) {
      const nickname = part.slice(1);
      const matchedUser = recipients.find(
        (user) =>
          user.preferredName.replace(/\s+/g, "").toLowerCase() ===
          nickname.toLowerCase()
      );
      if (matchedUser) {
        return (
          <Link
            to={`/profile/${matchedUser.id}`}
            className={styles.mentionLink}
            key={`mention-${index}`}
          >
            <span className={styles.mentionChip}>
              {matchedUser.preferredName}
            </span>
          </Link>
        );
      }
    }

    // Check #hashtag
    if (part.startsWith("#") && hashTagIds) {
      const tagName = part.slice(1);
      const matchedTag = hashTagIds.find(
        (t) => (t.hashTagName || "").toLowerCase() === tagName.toLowerCase()
      );
      if (matchedTag) {
        return (
          <span className={styles.hashtagChip} key={`hashtag-${index}`}>
            {matchedTag.hashTagName}
          </span>
        );
      }
    }

    // Check +points
    if (/^\+\d+$/.test(part)) {
      const amount = part.slice(1);
      return (
        <span className={styles.pointsChip} key={`points-${index}`}>
          {amount} pts
        </span>
      );
    }

    // Check $coins
    if (/^\$\d+$/.test(part)) {
      const amount = part.slice(1);
      return (
        <span className={styles.coinsChip} key={`coins-${index}`}>
          {amount} coins
        </span>
      );
    }

    // Normal text
    return <span key={index}>{part}</span>;
  });

  return <>{rendered}</>;
};

export default CommentMessage;
